import { useEffect, useState } from "react";
import api from "@/lib/api";
import { recall, assetUrl } from "@/lib/forms";
import { CalendloTimes, MeetNote } from "@/components/forms/Calendlo";
import BookedBurst from "@/components/forms/BookedBurst";
import { Video, Clock } from "lucide-react";

const ACCENT_DEF = "#e8e2d4";
const light = (hex) => { const h = (hex || "").replace("#", ""); if (h.length < 6) return false; const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)); return 0.299 * r + 0.587 * g + 0.114 * b > 150; };
const fmt = (s) => new Date(s).toLocaleString("en-IN", { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" }) + " IST";

function Fields({ fields, ans, setAns, ACCENT }) {
  return fields.map((f) => (
    <label key={f.id} className="block space-y-1">
      <span className="text-sm opacity-80">{f.label}{f.required && <span style={{ color: ACCENT }}> *</span>}</span>
      {f.type === "long_text" ? <textarea rows={3} className="w-full bg-transparent border border-[#e8e2d455] rounded-md p-2.5 text-sm" value={ans[f.id] || ""} onChange={(e) => setAns({ ...ans, [f.id]: e.target.value })} data-testid={`link-field-${f.id}`} />
        : f.type === "dropdown" ? (
          <select className="w-full bg-[#0d0d0c] border border-[#e8e2d455] rounded-md p-2.5 text-sm" value={ans[f.id] || ""} onChange={(e) => setAns({ ...ans, [f.id]: e.target.value })} data-testid={`link-field-${f.id}`}>
            <option value="">Select…</option>{(f.options || []).map((o, i) => <option key={`${i}-${o}`}>{o}</option>)}
          </select>)
          : <input type={f.type === "email" ? "email" : f.type === "phone" ? "tel" : "text"} className="w-full bg-transparent border border-[#e8e2d455] rounded-md p-2.5 text-sm"
            value={ans[f.id] || ""} onChange={(e) => setAns({ ...ans, [f.id]: e.target.value })} data-testid={`link-field-${f.id}`} />}
    </label>
  ));
}

export default function BookingPage({ slug }) {
  const [link, setLink] = useState(null);
  const [err, setErr] = useState("");
  const [pick, setPick] = useState(null);
  const [ans, setAns] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => { api.get(`/public/links/${slug}`).then((r) => { setLink(r.data); document.title = r.data.event_name || "Book a call"; }).catch(() => setErr("This page isn't available.")); }, [slug]);
  const book = async () => {
    setBusy(true); setMsg("");
    try { const { data } = await api.post(`/public/links/${slug}/book`, { start: pick, answers: ans }); setDone(data.meeting); }
    catch (e) { setMsg(e.response?.data?.detail || "Something went wrong. Please try again."); if (e.response?.status === 409) { setPick(null); setReload((n) => n + 1); } }
    finally { setBusy(false); }
  };
  const ACCENT = link?.page_accent || ACCENT_DEF;
  const bg = link?.page_bg || "#0d0d0c";
  const fg = light(bg) ? "#111111" : "#e8e2d4";
  const onAccent = light(ACCENT) ? "#000" : "#fff";
  const vars = { ...ans, first_name: String(ans.name || "").split(" ")[0] };
  const [started, setStarted] = useState(false);
  const name = link ? recall(link.event_name, vars) || "Discovery call" : "";
  const overview = link?.display_mode !== "calendar" && !started;
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-cover bg-center" data-testid="booking-page"
      style={{ color: fg, backgroundColor: bg, ...(link?.page_bg_image ? { backgroundImage: `linear-gradient(${bg}cc, ${bg}ee), url(${assetUrl(link.page_bg_image)})` } : {}) }}>
      <div className="w-full max-w-2xl space-y-5">
        {err ? <div className="text-lg" data-testid="booking-error">{err}</div> : !link ? <div className="opacity-60 text-sm">Loading…</div> : done ? (
          <div data-testid="booking-done">
            {done.success_text && <BookedBurst text={done.success_text} accent={ACCENT} />}
            <div className="text-sm opacity-80">{fmt(done.start)}</div>
            {done.meet_url && <a className="underline text-sm block mt-1" href={done.meet_url} target="_blank" rel="noreferrer" data-testid="booking-meet-link">{done.meet_url}</a>}
            <div className="text-xs opacity-60 mt-2">{done.confirmed ? "A calendar invite is on its way to your inbox." : "We'll send the calendar invite to your email shortly."}</div>
            <a className="text-xs underline opacity-70 mt-2 inline-block" href={done.manage_url} data-testid="booking-manage-link">Reschedule or cancel</a>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              {link.page_logo && <img src={assetUrl(link.page_logo)} alt="" className="h-8 w-auto" data-testid="booking-logo" />}
              <div className="text-xs uppercase tracking-widest opacity-50">{link.page_headline || "Thick & Thin"}</div>
            </div>
            <div className="flex items-center gap-4">
              {link.page_photo && <img src={assetUrl(link.page_photo)} alt="" className="w-14 h-14 rounded-full object-cover" data-testid="booking-photo" />}
              <div className="text-3xl font-medium" data-testid="booking-title">{name}</div>
            </div>
            <div className="flex gap-4 text-sm opacity-80"><span className="inline-flex items-center gap-1.5"><Clock size={14} />{link.meeting_duration_min || 30} min</span><span className="inline-flex items-center gap-1.5"><Video size={14} />Google Meet</span></div>
            {link.event_description && <div className="text-sm opacity-70 whitespace-pre-line">{recall(link.event_description, vars)}</div>}
            <MeetNote />
            {overview ? <div><button className="px-5 py-2.5 rounded-md text-sm font-medium mt-2" style={{ background: ACCENT, color: onAccent }} onClick={() => setStarted(true)} data-testid="booking-pick-time">Pick a time</button></div>
              : !pick ? <CalendloTimes slotsUrl={`/public/links/${slug}/slots`} value={pick} onChange={setPick} accent={ACCENT} reloadKey={reload} /> : (
              <div className="space-y-4" data-testid="booking-details">
                <div className="text-sm"><b>{fmt(pick)}</b> <button className="underline opacity-70 ml-2" onClick={() => setPick(null)} data-testid="booking-change-time">Change</button></div>
                <Fields fields={link.fields || []} ans={ans} setAns={setAns} ACCENT={ACCENT} />
                <button disabled={busy} className="px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-40" style={{ background: ACCENT, color: onAccent }} onClick={book} data-testid="booking-confirm">{busy ? "Booking…" : "Book the call"}</button>
              </div>
            )}
            {msg && <div className="text-sm text-[#ff8a7a]" data-testid="booking-msg">{msg}</div>}
          </>
        )}
      </div>
    </div>
  );
}
